import { EventAggregator } from 'aurelia-event-aggregator';
import {
	autoinject,
	BindingEngine,
	bindingMode,
	computedFrom,
	customElement,
	PLATFORM,
	useView,
} from 'aurelia-framework';
import { bindable } from 'aurelia-templating';

import { Field } from '~/components/field/common/field';
import { Form } from '~/components/form/form';
import { CodeAndDescriptionPairValueConverter } from '~/converters/code-to-description';
import { PresentationType } from '~/converters/presentation-type';
import {
	commonFieldProperties,
	registerComponent,
} from '~/forms/customisation-editor/property-meta';
import { FieldModel } from '~/models/form-model';
import { Logger } from '~/utils/logger';

type OptionValue = boolean | string;

export interface Option<T> {
	displayName: string;
	value: T;

	/**
	 * Used to show a description next to a "<a-select>" field when it's in read-only mode.
	 * See "field.html" to see how this is rendered.
	 */
	description?: string;
}

export interface SelectFieldModel extends FieldModel {
	onChange?: (value: string) => void;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface SelectOption extends Option<OptionValue> {}

const componentTagName = 'a-select';

registerComponent(componentTagName, {
	name: 'Select',
	requirePath: PLATFORM.moduleName('components/field/common/select'),
	props: commonFieldProperties,
	isSelectable: true,
});

@autoinject
@customElement(componentTagName)
@useView(PLATFORM.moduleName('./select.html'))
export class Select extends Field {
	/**
	 * If "fieldModel" is not set, this property can be used to configure the available options a user
	 * can select from.
	 */
	@bindable private options: SelectOption[] | string[];

	@bindable protected isLabelLeft: boolean = false;

	/**
	 * onChange will fire when a *user* triggers a value change with the select field.
	 */
	@bindable private onChange?: (value: string) => void;

	/**
	 * allowOptionsToOverrideFieldModel is an escape-hatch to be utilized for edge-cases where you want
	 * to utilize the fieldModel and options at the same time.
	 *
	 * For example, Insurance Summary leverages this so the right-click context menu works with its dynamic
	 * updating of data.
	 */
	@bindable({
		defaultBindingMode: bindingMode.toView,
	})
	private allowOptionsToOverrideFieldModel: boolean = false;

	/**
	 * showEmptyOption provides an empty option  on dropdown list
	 * Always add an empty selected option at the top.
	 * This is to prevent 'Unsaved changes' dialog when user hasn't modified anything in ADD form.
	 */
	@bindable private showEmptyOption: boolean = true;

	/**
	 * This hint is displayed on an empty select field when the description is empty.
	 * It is different to a placeholder because it only displays when the field is read-only and
	 * because it is a description, it is not added as a possible value in the select dropdown.
	 */
	@bindable private selectHint: string = '';

	private selectOptionsError: string = '';

	constructor(
		private codeAndDescriptionPair: CodeAndDescriptionPairValueConverter,
		public element: Element,
		protected parentForm: Form,
		protected bindingEngine: BindingEngine,
		readonly eventAggregator: EventAggregator,
	) {
		super(Field.TYPE.SELECT, element, parentForm, eventAggregator);
		this.isDisableInputMasks = true;
	}

	/**
	 * selectOptionsMatcher is used by the select.html template to determine what
	 * value is currently selected.
	 */
	private selectOptionsMatcher = (
		a: OptionValue,
		b: OptionValue,
	): boolean => {
		// !!!!WARNING!!!!
		// NOTE: Jake: 2019-05-07
		// This matcher seems to automatically casts boolean values to a string.
		// Had to change customisation editor code so boolean values are strings.
		return a === b;
	};

	protected fieldModelChanged(fieldModel: FieldModel) {
		super.fieldModelChanged(fieldModel);
		if (fieldModel) {
			// NOTE: Jake: 2019-04-14 - WEBUI-1239
			// We can't get the description text from the list of options
			// if we don't have the "fieldModel.codeList" yet. So we update here.
			this.updateDescription();
		}
	}

	protected valueChanged(newValue, oldValue) {
		super.valueChanged(newValue, oldValue);
		this.updateDescription();
	}

	private readonly onSelectChange = (value: string): void => {
		if (this.onChange) {
			this.onChange(value);
		}

		const selectFieldModel = this.fieldModel as SelectFieldModel;
		if (selectFieldModel && selectFieldModel.onChange) {
			selectFieldModel.onChange(value);
		}
	};

	@computedFrom(
		'options',
		'value',
		'fieldModel',
		'fieldModel.codeList',
		'fieldModel.presentationType',
	)
	private get selectOptions(): SelectOption[] {
		if (
			!this.allowOptionsToOverrideFieldModel &&
			this.options &&
			this.options.length > 0 &&
			this.fieldModel &&
			this.fieldModel.dataType
		) {
			throw new Error(
				'Should not use "fieldModel.dataType" and "options" at the same time. There is however an escape hatch "allowOptionsToOverrideFieldModel" to allow this for edge cases, please read documentation before leveraging.',
			);
		}
		const options: SelectOption[] = [];
		if (this.options && this.options.length > 0) {
			for (const option of this.options) {
				if (typeof option === 'string') {
					options.push({
						displayName: option,
						value: option,
					});
					continue;
				}
				options.push(option);
			}
		} else if (this.fieldModel) {
			if (this.showEmptyOption) {
				const blankValue =
					this.fieldModel.dataType &&
					this.fieldModel.dataType.name === 'Character'
						? ' '
						: '';
				options.push({
					displayName: this.placeholder ? this.placeholder : '',
					value: blankValue,
				});
			}

			// Handle transformation of fieldModel data format to options
			if (this.fieldModel.codeList) {
				for (const option of this.fieldModel.codeList) {
					const displayName = this.codeAndDescriptionPair.toView(
						option,
						this.fieldModel.presentationType
							? this.fieldModel.presentationType
							: undefined,
					);
					// by default, select value should be set to code value
					const selectOption: SelectOption = {
						displayName,
						value: option.code,
					};
					// if presentationType is DESCRIPTION, eg Title fields, code has been swapped
					// with description so select value should be set to description value
					if (
						this.fieldModel.presentationType ===
						PresentationType.DESCRIPTION
					) {
						selectOption.value = option.description;
					}
					// only display description if presentationType is not CODE or DESCRIPTION
					if (
						this.fieldModel.presentationType !==
							PresentationType.CODE &&
						this.fieldModel.presentationType !==
							PresentationType.DESCRIPTION
					) {
						selectOption.description = option.description;
					}
					options.push(selectOption);
				}
			}
			if (this.value) {
				// NOTE: Jake: 2019-01-15 - WEBUI-1239
				// By design you *should* provide all the options the user is allowed to select.
				// I did not want to simply add the value to the list as this might allow a
				// a backend security or UX problem to go unnoticed.
				// ie. A user changes the value of something but then cannot change it back because
				//     the option would go away on page-reload.
				let hasValueInList: boolean = false;
				for (const option of options) {
					hasValueInList =
						hasValueInList ||
						option.value === this.value ||
						(this.fieldModel.presentationType &&
							this.fieldModel.presentationType ===
								PresentationType.DESCRIPTION &&
							option.description === this.value);
				}
				if (!hasValueInList) {
					const errorMessage =
						'Error: ' +
						this.fieldModel.name +
						': current "value" is not an available <option>. (value: ' +
						this.value +
						', string length: ' +
						String(this.value).length +
						')';
					if (this.selectOptionsError !== errorMessage) {
						this.selectOptionsError = errorMessage;
						Logger.error(
							this.selectOptionsError,
							'options: ',
							options,
							' current readonly state: ',
							this.isFieldReadonly,
						);
					}
					return [];
				}
			}
		}
		// Remove error message if no error occurred
		this.selectOptionsError = '';
		return options;
	}

	/**
	 * Update description based from the selected value
	 */
	private updateDescription = (): void => {
		this.description = '';
		if (this.selectOptions) {
			for (const option of this.selectOptions) {
				if (this.selectOptionsMatcher(this.value, option.value)) {
					if (option.description) {
						this.description = option.description;
					}
					break;
				}
			}
		}
	};
}
